import React, {useEffect, useReducer, useRef, useState} from 'react';
import { hot } from 'react-hot-loader';
import ProfileFooter from '../ProfileFooter';
import StickyFooterLayout from 'common/StickyFooterLayout';
import StaffProfileContainer from './StaffProfileContainer';
import { SFEntityID, SFNotification, StaffProfilePrivileges } from 'utils/types';
import useStarfishAPI from 'hooks/useStarfishAPI';
import { urlParams } from 'utils/request';
import PropTypes from 'prop-types';
import Toast from 'common/Toast';
import ConfirmDialog from 'common/ConfirmDialog';
import { saveInstructorNotificationPreferences, saveInstructorProfile, saveAppointmentPreferences } from 'utils/profile';
import { withNotification } from 'common/withNotification';
import { initialState, reducer } from './reducer';
import { isEmptyString } from 'utils/text';
import { hasInvalidSchedulingDeadline, isValidStaffTitle, isStringOverCharLimit} from '../profileValidation';
import { merge } from 'lodash';
import css from './App.scss';

/**
 * @param {object} props - see propTypes
 * @returns {node} the student's profile
 */
function App(props) {
    const [state, dispatch] = useReducer(reducer, initialState);

    const [profileResponse, callProfileAPI] = useStarfishAPI();
    const [notificationPreferencesResponse, callNotificationPreferencesAPI] = useStarfishAPI();
    const [appointmentPreferencesResponse, callAppointmentPreferencesAPI] = useStarfishAPI();

    const [saveProfileResponse, callProfileSaveAPI] = useStarfishAPI();
    const [saveNotificationPreferencesResponse, callNotificationPreferencesSaveAPI] = useStarfishAPI();
    const [saveAppointmentPreferencesResponse, callAppointmentPreferencesSaveAPI] = useStarfishAPI();

    const [saveOutlookCalendarSyncResponse, saveOutlookCalendarSyncAPI] = useStarfishAPI();
    const [outlookCalendarPreferencesResponse, callOutlookCalendarPreferencesAPI] = useStarfishAPI();

    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const contentRef = useRef();

    useEffect(() => {
        callProfileAPI('../instructor/getInstitutionProfile.html', {
            method: 'POST',
            body: urlParams({ instructorId: props.userId })
        });
        callNotificationPreferencesAPI('../instructor/getNotificationPreferences.html', {
            method: 'POST',
            body: urlParams({ instructorId: props.userId })
        });
        props.privileges.showAppointmentPreferencesTab && callAppointmentPreferencesAPI('../instructor/getAppointmentsPreferences.html', {
            method: 'POST',
            body: urlParams({ instructorId: props.userId })
        });
    }, [props.userId]);

    useEffect(() => {
        if (profileResponse.success && profileResponse.data.result)
            dispatch({ type: 'LOAD_USER_PROFILE', elements: profileResponse.data.result[0] });
    }, [profileResponse]);

    useEffect(() => {
        if (notificationPreferencesResponse.success && notificationPreferencesResponse.data.result)
            dispatch({ type: 'LOAD_NOTIFICATION_PREFERENCES', elements: notificationPreferencesResponse.data.result[0] });
    }, [notificationPreferencesResponse]);

    useEffect(() => {
        if (appointmentPreferencesResponse.success && appointmentPreferencesResponse.data.data)
            dispatch({ type: 'LOAD_APPOINTMENT_PREFERENCES', elements: appointmentPreferencesResponse.data.data[0] });
    }, [appointmentPreferencesResponse]);


    const onSave = () => {
        saveInstructorProfile(callProfileSaveAPI, props.userId, state.userProfile);
        saveInstructorNotificationPreferences(
            callNotificationPreferencesSaveAPI,
            props.userId,
            state.notificationPreferences);
        props.privileges.showAppointmentPreferencesTab && saveAppointmentPreferences(callAppointmentPreferencesSaveAPI, props.userId, state.appointmentPreferences);
    };

    useEffect(() => {
        // Check for appointmentPreference only when the Appointment Preference tab is shown
        const saveSuccess = saveProfileResponse.success && saveNotificationPreferencesResponse.success &&
            (!props.privileges.showAppointmentPreferencesTab || saveAppointmentPreferencesResponse.success);

        const saveError = saveNotificationPreferencesResponse.error || saveProfileResponse.error ||
            (props.privileges.showAppointmentPreferencesTab && saveAppointmentPreferencesResponse.error);

        if (saveSuccess) {
            dispatch({type: 'CHANGES_SAVED'});
        } else if (saveError) {
            setErrorMessage(saveError);
            setShowError(true);
        }
    }, [saveProfileResponse, saveNotificationPreferencesResponse, saveAppointmentPreferencesResponse]);

    const getToast = () => {
        if (!state.showToast) return null;
        return <Toast className={css['sticky-footer-toast']}
                      message={state.toastText}
                      onTimeout={() => dispatch({ type: 'HIDE_TOAST' })} />
    };

    const externalCalendarPreferenceSync = async (updatedExchangeImpEnabled) => {
        // Selectively saving and displaying only the updated exchange information
        const exchangeNotificationPreferences = merge(
            {},
            state.originalNotificationPreferences,
            {exchangeFreeBusyEnabled: updatedExchangeImpEnabled});
        await saveInstructorNotificationPreferences(saveOutlookCalendarSyncAPI, props.userId, exchangeNotificationPreferences);

        callOutlookCalendarPreferencesAPI('../instructor/getNotificationPreferences.html', {
            method: 'POST',
            body: urlParams({ instructorId: props.userId })
        });
    };

    useEffect(() => {
        // When staff selects their exchange impersonation selection from the pop up, the webhook exchange section shows
        // the updated impersonation workflow status and proper opt-in/opt-out link immediately. This needs a backend
        // call to save and get the updated result.
        // We use the same api as notification preferences, but save and update only the exchange impersonation data.
        if (saveOutlookCalendarSyncResponse.success && outlookCalendarPreferencesResponse.success)
            dispatch({ type: 'EXCHANGE_SYNC', elements: outlookCalendarPreferencesResponse.data.result[0] });
    }, [outlookCalendarPreferencesResponse]);

    // Don't show an error or prevent saving if the feature is disabled
    const invalidPronouns = () => props.privileges.showPronouns
        && props.privileges.canEditPronouns
        && isStringOverCharLimit(state.userProfile.pronouns, 25);

    const getContent = () => (
        <div>
            <StaffProfileContainer {...props}
                                   appointmentPreferences={state.appointmentPreferences}
                                   contentRef={contentRef}
                                   notificationPreferences={state.notificationPreferences}
                                   originalProfile={state.originalUserProfile}
                                   profile={state.userProfile}
                                   invalidPronouns={invalidPronouns()}
                                   externalCalendarPreferenceSync={externalCalendarPreferenceSync}
                                   setValue={(values) => dispatch({ type: 'SET_VALUE', elements: values })} />
            {getToast()}
            <ConfirmDialog confirmText="Clear Changes"
                           cancelText="Cancel"
                           headerText="Are you sure you want to clear all changes?"
                           descriptionText="You will not be able to recover any changes you made to your profile."
                           isOpen={state.showCancelDialog}
                           onClose={() => dispatch({ type: 'HIDE_CANCEL_DIALOG' })}
                           onConfirm={() => dispatch({ type: 'RESET_CHANGES' })} />
            <ConfirmDialog headerText="Something went wrong, changes not saved"
                           descriptionText={errorMessage}
                           onConfirm={() => {setShowError(false); setErrorMessage(null);}}
                           confirmText="OK"
                           isOpen={showError} />
        </div>
    );

    const validateBeforeSave = () => {
        return !!(isEmptyString(state.userProfile.correspondenceTo)
            || hasInvalidSchedulingDeadline(state.appointmentPreferences)
            || !isValidStaffTitle(state.userProfile.title)
            || invalidPronouns());
    };

    const getFooter = () => (
        <ProfileFooter disableSave={validateBeforeSave()}
                       onCancel={() => dispatch({ type: 'SHOW_CANCEL_DIALOG' })}
                       onSave={onSave} 
                       saving={saveProfileResponse.pending || saveNotificationPreferencesResponse.pending || 
                                (props.privileges.showAppointmentPreferencesTab && saveAppointmentPreferencesResponse.pending)} />
    );

    if (!state.userProfile || profileResponse.pending
        || !state.notificationPreferences || notificationPreferencesResponse.pending
        || (props.privileges.showAppointmentPreferencesTab && (!state.appointmentPreferences || appointmentPreferencesResponse.pending))) {
        return <div>Loading...</div>;
    }

    return <StickyFooterLayout className={css['container']}
                               content={getContent()}
                               footer={getFooter()}
                               ref={contentRef} />;
}

App.defaultProps = {
    privileges: {
        allowPhotoUpload: false,
        allowPreferredEmail: false,
        calendarLockingEnabled: false,
        canAssignTrackingItems: false,
        canEditPronouns: false,
        canShareLinks: false,
        exchangeIntegrationEnabled: false,
        hasConnect: false,
        hasEarlyAlert: false,
        hasStudentRole: false,
        isCalendarManager: false,
        shouldSeeAttendanceTracking: false,
        shouldSeeZoomIn: false,
        shouldSeeInterventions: false,
        shouldSeeSurveys: false,
        showAppointmentPreferencesTab: false,
        showExchangeImpersonationSection: false,
        showMobileEmailInfo: false,
        showPronouns: false,
        textMessagingEnabled: false,
        weekendReminderEmailSettingEnabled: false
    }
};

App.propTypes = {
    isCalendarLocked: PropTypes.bool,
    isIE: PropTypes.bool,
    exchangeAccounts: PropTypes.string,
    exchangeImpersonationPopUp: PropTypes.func,
    mobileEmailInfoLink: PropTypes.string,
    notification: SFNotification.isRequired,
    photoAcceptTypes: PropTypes.string,
    privileges: StaffProfilePrivileges,
    scheduleId: PropTypes.number,
    supportedCountries: PropTypes.string,
    userId: SFEntityID.isRequired,
    validatePhoto: PropTypes.func.isRequired
};

export default hot(module)(withNotification(App));
